/**
 * QuickFilterBar — pasek szybkich filtrów nad listą (chipy + przepełnienie do
 * dropdowna). Renderuje presety z `list_config.quickFilters`; klik chipa
 * aktywuje preset (caller aplikuje jego warunki do zapytania). „Wszystkie"
 * czyści wybór.
 *
 * Forma: pierwsze N presetów jako chipy, reszta pod „Więcej ▾" (decyzja UX
 * 2026-06-01 — hybryda chipy + overflow). Wzorzec danych z SellCall, ale
 * SellCall używał czystego dropdowna — my wybraliśmy chipy dla widoczności.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuickFilter } from '@saas/shared';
import { ChevronDown } from 'lucide-react';

const MAX_VISIBLE_CHIPS = 4;

interface QuickFilterBarProps {
  quickFilters: QuickFilter[];
  activeId: string | null;
  onChange: (id: string | null) => void;
}

export function QuickFilterBar({
  quickFilters,
  activeId,
  onChange,
}: QuickFilterBarProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [overflowOpen]);

  if (quickFilters.length === 0) return null;

  const sorted = [...quickFilters].sort((a, b) => a.order - b.order);
  const visible = sorted.slice(0, MAX_VISIBLE_CHIPS);
  const overflow = sorted.slice(MAX_VISIBLE_CHIPS);

  const chipClass = (isActive: boolean) =>
    `inline-flex items-center rounded-full border px-3 py-1 text-sm transition-colors ${
      isActive
        ? 'border-accent-600 bg-accent-50 text-accent-700 dark:bg-accent-900/20 dark:text-accent-300'
        : 'border-border-secondary text-text-secondary hover:bg-bg-secondary'
    }`;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="quick-filter-bar">
      <button
        type="button"
        onClick={() => onChange(null)}
        className={chipClass(activeId === null)}
        data-active={activeId === null ? 'true' : 'false'}
        data-testid="quick-filter-all"
      >
        {t('tenant.listBuilder.qf.all')}
      </button>

      {visible.map((qf) => (
        <button
          key={qf.id}
          type="button"
          onClick={() => onChange(qf.id)}
          className={chipClass(activeId === qf.id)}
          data-active={activeId === qf.id ? 'true' : 'false'}
          data-testid={`quick-filter-chip-${qf.id}`}
        >
          {qf.label}
        </button>
      ))}

      {overflow.length > 0 && (
        <div className="relative" ref={overflowRef}>
          <button
            type="button"
            onClick={() => setOverflowOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
            className={chipClass(overflow.some((q) => q.id === activeId))}
            data-active={overflow.some((q) => q.id === activeId) ? 'true' : 'false'}
            data-testid="quick-filter-more"
          >
            {t('tenant.listBuilder.qf.more')}
            <ChevronDown size={14} aria-hidden="true" className="ml-1" />
          </button>
          {overflowOpen && (
            <div
              role="menu"
              className="absolute left-0 z-50 mt-1 min-w-[180px] rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
            >
              {overflow.map((qf) => (
                <button
                  key={qf.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onChange(qf.id);
                    setOverflowOpen(false);
                  }}
                  className={`block w-full px-3 py-1.5 text-left text-sm transition-colors ${
                    activeId === qf.id
                      ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/20'
                      : 'text-text-primary hover:bg-bg-secondary'
                  }`}
                  data-testid={`quick-filter-overflow-${qf.id}`}
                >
                  {qf.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
